import { Group, Pagination, ScrollArea, Table, Text, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons-react";
import { type ReactNode, useMemo, useState } from "react";

import { EmptyState } from "./EmptyState";

export type Column<T> = {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** Enables client-side sorting on this column. */
  sortValue?: (row: T) => string | number | bigint | null | undefined;
  align?: "left" | "right" | "center";
  width?: number | string;
};

export type ServerPaging = {
  limit: number;
  offset: number;
  onChange: (offset: number) => void;
  /** Total rows when the API reports it; otherwise paging uses `rows.length === limit` as "has more". */
  total?: number;
};

type Props<T> = {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  loading?: boolean;
  empty?: ReactNode;
  paging?: ServerPaging;
  testId?: string;
};

/** Mantine table with client sort and optional limit/offset server paging. */
export function DataTable<T>({ columns, rows, rowKey, onRowClick, loading, empty, paging, testId }: Props<T>) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);

  const sorted = useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!col?.sortValue || !sort) return rows;
    const get = col.sortValue;
    return [...rows].sort((a, b) => {
      const x = get(a);
      const y = get(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      return (x < y ? -1 : 1) * sort.dir;
    });
  }, [rows, columns, sort]);

  const toggle = (key: string) =>
    setSort((s) =>
      s?.key !== key ? { key, dir: 1 }
      : s.dir === 1 ? { key, dir: -1 }
      : null,
    );

  const page = paging ? Math.floor(paging.offset / paging.limit) + 1 : 1;
  const pages =
    paging ?
      paging.total !== undefined ?
        Math.max(1, Math.ceil(paging.total / paging.limit))
      : page + (rows.length >= paging.limit ? 1 : 0)
    : 1;

  return (
    <div data-testid={testId}>
      <ScrollArea>
        <Table highlightOnHover={Boolean(onRowClick)} verticalSpacing="sm" style={{ opacity: loading ? 0.6 : 1 }}>
          <Table.Thead>
            <Table.Tr>
              {columns.map((c) => (
                <Table.Th key={c.key} ta={c.align} w={c.width}>
                  {c.sortValue ?
                    <UnstyledButton onClick={() => toggle(c.key)} fw={600} fz="sm">
                      <Group gap={4} wrap="nowrap" justify={c.align === "right" ? "flex-end" : undefined}>
                        {c.header}
                        {sort?.key === c.key ?
                          sort.dir === 1 ?
                            <IconChevronUp size={14} />
                          : <IconChevronDown size={14} />
                        : <IconSelector size={14} opacity={0.5} />}
                      </Group>
                    </UnstyledButton>
                  : c.header}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((row) => (
              <Table.Tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: "pointer" } : undefined}
                data-testid="table-row"
              >
                {columns.map((c) => (
                  <Table.Td key={c.key} ta={c.align}>
                    {c.render(row)}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      {!loading && rows.length === 0 && (empty ?? <EmptyState />)}
      {paging && pages > 1 && (
        <Group justify="space-between" mt="md">
          <Text size="sm" c="dimmed">
            Page {page}
            {paging.total !== undefined ? ` of ${pages}` : ""}
          </Text>
          <Pagination size="sm" total={pages} value={page} onChange={(p) => paging.onChange((p - 1) * paging.limit)} />
        </Group>
      )}
    </div>
  );
}
